import { useState, useEffect, useCallback } from "react";
import api from "@/utils/api";
import { formatApiError, isAuthError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, AlertCircle, Copy, Building2, CreditCard, Banknote, RefreshCw, ShieldCheck, AlertTriangle, UserPlus, Trash2, Send, Mail, UserCog, Pencil, Archive, ArchiveRestore, Database, PlayCircle, ShieldOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MemberProfile from "@/components/MemberProfile";
import BulkImportSection from "@/components/BulkImportSection";
import PdfZipImportSection from "@/components/PdfZipImportSection";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LearnerEmailsSwitch } from "@/components/LearnerEmailsSwitch";
import { Switch } from "@/components/ui/switch";
import { BrandingSettings } from "@/components/BrandingSettings";
import { useBranding } from "@/contexts/BrandingContext";
import { useAuth } from "@/contexts/AuthContext";
import { isAdmin as isAdminRole, isSuperAdmin, ROLE_BADGE_STYLES, assignableRoles } from "@/utils/roles";
import useDocumentTitle from "@/hooks/useDocumentTitle";

export default function SettingsPage() {
  useDocumentTitle("Settings", "Company info, Stripe, backups, team and bulk import.");
  const { user } = useAuth();
  const isAdmin = isAdminRole(user?.role);
  const isSuper = isSuperAdmin(user?.role);
  const { lms_name, lms_short_name } = useBranding();
  const canAssignRoles = assignableRoles(user?.role);
  const [company, setCompany] = useState({});
  const [stripe, setStripe] = useState({});
  const [events, setEvents] = useState([]);
  const [invites, setInvites] = useState([]);
  const [team, setTeam] = useState([]);
  const [profileId, setProfileId] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", role: "staff", message: "" });
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [backups, setBackups] = useState(null);
  const [backupRunning, setBackupRunning] = useState(false);

  const fetchAll = useCallback(async () => {
    // Use allSettled so a single transient failure (e.g. Cloudflare 520
    // during deploy) doesn't blank out the entire page.
    const [companyRes, stripeRes] = await Promise.allSettled([
      api.get("/settings/company"),
      api.get("/settings/stripe"),
    ]);
    if (companyRes.status === "fulfilled") {
      setCompany(companyRes.value.data);
    } else if (!isAuthError(companyRes.reason)) {
      console.error("Failed to load company settings:", companyRes.reason?.response?.status, companyRes.reason?.message);
      toast.error("Couldn't load company info — refresh in a moment");
    }
    if (stripeRes.status === "fulfilled") {
      setStripe(stripeRes.value.data);
    } else if (!isAuthError(stripeRes.reason)) {
      console.error("Failed to load Stripe settings:", stripeRes.reason?.response?.status, stripeRes.reason?.message);
    }
    // Fetch team (staff/admin only). Filter server-side so it works regardless
    // of how many student records exist — a large members DB where students
    // dominate the newest-first sort would otherwise push the whole team past
    // the `limit` cap and hide them from the UI.
    try {
      const { data: t } = await api.get("/members?role=superadmin,admin,staff&status=all&limit=1000");
      setTeam(t.members || []);
    } catch (err) {
      if (!isAuthError(err)) console.error("Failed to load team members:", err);
    }
    if (isAdmin) {
      try {
        const { data: ev } = await api.get("/settings/stripe/events?limit=10");
        setEvents(ev);
      } catch (err) {
        if (!isAuthError(err)) console.error("Failed to load Stripe events:", err);
      }
      try {
        const { data: inv } = await api.get("/invitations");
        setInvites(inv);
      } catch (err) {
        if (!isAuthError(err)) console.error("Failed to load invitations:", err);
      }
      try {
        const { data: bk } = await api.get("/backups/status");
        setBackups(bk);
      } catch (err) {
        if (!isAuthError(err)) console.error("Failed to load backups status:", err);
      }
    }
    setLoading(false);
  }, [isAdmin]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const set = (k, v) => setCompany(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/company", company);
      setCompany(data);
      toast.success("Company info saved — receipts and invoices updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const testStripe = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/settings/stripe/test");
      setTestResult(data);
      if (data.ok) toast.success(`Stripe connection OK${data.livemode ? " (LIVE MODE)" : " (test mode)"}`);
      else toast.error("Stripe test failed: " + (data.error || "unknown"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); }
    catch (err) { console.error("Clipboard write failed:", err); toast.error("Couldn't copy"); }
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.email) { toast.error("Email is required"); return; }
    setInviteSubmitting(true);
    try {
      const { data } = await api.post("/invitations", inviteForm);
      toast.success(`Invitation sent to ${data.email}${data.email_sent ? "" : " (email failed — share link manually)"}`);
      setInviteOpen(false);
      setInviteForm({ email: "", name: "", role: "staff", message: "" });
      const { data: inv } = await api.get("/invitations");
      setInvites(inv);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setInviteSubmitting(false);
    }
  };

  const revokeInvite = async (inv) => {
    const verb = inv.status === "pending" ? "Revoke" : "Delete";
    if (!window.confirm(`${verb} invitation to ${inv.email}?`)) return;
    try {
      await api.delete(`/invitations/${inv.id}`);
      toast.success(`Invitation ${inv.status === "pending" ? "revoked" : "deleted"}`);
      const { data } = await api.get("/invitations");
      setInvites(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const resendInvite = async (inv) => {
    try {
      await api.post(`/invitations/${inv.id}/resend`);
      toast.success(`Invitation resent to ${inv.email}`);
      const { data } = await api.get("/invitations");
      setInvites(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const copyInviteLink = (inv) => {
    const url = `${window.location.origin}/accept-invite/${inv.token}`;
    copy(url);
  };

  const runBackupNow = async () => {
    setBackupRunning(true);
    try {
      const { data } = await api.post("/backups/run");
      toast.success(`Backup OK · ${(data.size_bytes / 1024 / 1024).toFixed(2)} MiB`);
      const { data: bk } = await api.get("/backups/status");
      setBackups(bk);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Backup failed");
    } finally {
      setBackupRunning(false);
    }
  };

  const refetchTeam = async () => {
    try {
      const { data: t } = await api.get("/members?role=superadmin,admin,staff&status=all&limit=1000");
      setTeam(t.members || []);
    } catch (err) {
      if (!isAuthError(err)) console.error("Failed to refresh team:", err);
    }
  };

  const archiveMember = async (m) => {
    try {
      if (m.status === "archived") {
        await api.post(`/members/${m.id}/unarchive`);
        toast.success("Restored");
      } else {
        if (!window.confirm(`Archive ${m.name}?`)) return;
        await api.post(`/members/${m.id}/archive`);
        toast.success("Archived");
      }
      refetchTeam();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteMember = async (m) => {
    if (!window.confirm(`Permanently delete ${m.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/members/${m.id}`);
      toast.success("Deleted");
      refetchTeam();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleRetrofit = async (m, value) => {
    try {
      await api.put(`/members/${m.id}/retrofit-access`, { retrofit_access: value });
      toast.success(`${m.name || m.email} ${value ? "can now access" : "no longer has access to"} ${lms_name}`);
      refetchTeam();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Update failed"); }
  };

  const roleBadge = { ...ROLE_BADGE_STYLES, admin: "bg-sky-100 text-sky-700 border-sky-200", staff: "bg-emerald-100 text-emerald-700 border-emerald-200" };

  const resetMfa = async (m) => {
    if (!window.confirm(`Reset two-factor authentication for ${m.name || m.email}? They'll sign in with just their password until they set it up again.`)) return;
    try { await api.post(`/members/${m.id}/mfa-reset`); toast.success("Two-factor authentication reset"); refetchTeam(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Reset failed"); }
  };

  const requestedTab = new URLSearchParams(window.location.search).get("tab");
  const initialTab = isSuper ? (requestedTab || "company") : (["team", "invitations"].includes(requestedTab) ? requestedTab : "team");
  const locked = (m) => !isSuper && ["admin", "superadmin"].includes(m.role);

  if (loading) return <div className="p-8 text-center text-muted-foreground">Loading settings...</div>;

  return (
    <div className="space-y-6 max-w-6xl mx-auto w-full" data-testid="settings-page">
      <Tabs defaultValue={initialTab} className="space-y-6">
        <TabsList className="flex flex-wrap h-auto p-1 bg-slate-100 w-full justify-start gap-1" data-testid="settings-tabs">
          {isSuper && <TabsTrigger value="company" data-testid="tab-company" className="data-[state=active]:bg-white">Company & Banking</TabsTrigger>}
          <TabsTrigger value="team" data-testid="tab-team" className="data-[state=active]:bg-white">Team Members</TabsTrigger>
          {isAdmin && <TabsTrigger value="invitations" data-testid="tab-invitations" className="data-[state=active]:bg-white">Staff Invitations</TabsTrigger>}
          {isSuper && <TabsTrigger value="stripe" data-testid="tab-stripe" className="data-[state=active]:bg-white">Stripe</TabsTrigger>}
          {isSuper && <TabsTrigger value="learner-emails" data-testid="tab-learner-emails" className="data-[state=active]:bg-white">Learner emails</TabsTrigger>}
          {isSuper && <TabsTrigger value="import" data-testid="tab-import" className="data-[state=active]:bg-white">Bulk Import</TabsTrigger>}
          {isSuper && <TabsTrigger value="database" data-testid="tab-database" className="data-[state=active]:bg-white">Database</TabsTrigger>}
        </TabsList>

        <TabsContent value="learner-emails" className="space-y-6 mt-0"><LearnerEmailsSwitch canEdit={isSuper} /></TabsContent>
        <TabsContent value="stripe" className="space-y-6 mt-0">
      {/* Stripe panel */}
      <section className="bg-white rounded-lg border border-border" data-testid="stripe-section">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <CreditCard size={18} className="text-slate-600" />
          <h2 className="font-heading font-semibold">Stripe Connection</h2>
          {stripe?.enabled === false && (
            <span className="ml-2 text-[11px] uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-200 rounded px-2 py-0.5" data-testid="stripe-disabled-pill">
              Disabled
            </span>
          )}
        </div>
        <div className="p-5 space-y-4">
          {stripe?.enabled === false && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="stripe-disabled-banner">
              <strong>Stripe is currently switched off.</strong> Online card checkout, pay-link buttons and Stripe-triggered receipt emails are all disabled.
              Manual payments (mark paid, PDF invoices/receipts, invoice emails) continue to work normally. To turn Stripe back on, set <code className="font-mono text-xs bg-white border rounded px-1">STRIPE_ENABLED=true</code> in <code className="font-mono text-xs bg-white border rounded px-1">backend/.env</code> and redeploy.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <StatusTile
              label="API Key"
              value={stripe?.mode === "live" ? "LIVE" : stripe?.mode === "test" ? "TEST MODE" : "MISSING"}
              tone={stripe?.mode === "live" ? "live" : stripe?.mode === "test" ? "warn" : "bad"}
              hint={stripe?.key_prefix || "Set STRIPE_API_KEY in backend/.env"}
              testId="stripe-key-tile"
            />
            <StatusTile
              label="Webhook Signing Secret"
              value={stripe?.webhook_secret_set ? "CONFIGURED" : "NOT SET"}
              tone={stripe?.webhook_secret_set ? "live" : "bad"}
              hint={stripe?.webhook_secret_set ? "STRIPE_WEBHOOK_SECRET loaded" : "Set STRIPE_WEBHOOK_SECRET in backend/.env after creating endpoint in Stripe Dashboard"}
              testId="stripe-secret-tile"
            />
            <StatusTile
              label="Last Event"
              value={stripe?.last_event ? stripe.last_event.event_type : "NONE"}
              tone={stripe?.last_event ? (stripe.last_event.processed ? "live" : "warn") : "neutral"}
              hint={stripe?.last_event?.received_at || "No webhook events received yet"}
              testId="stripe-last-event-tile"
            />
          </div>

          {/* Webhook URL row */}
          <div className="bg-slate-50 rounded p-3 space-y-2 border border-border">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium">Webhook Endpoint URL <span className="text-muted-foreground font-normal">— register this in Stripe Dashboard</span></Label>
              <Button size="sm" variant="ghost" className="h-7" onClick={() => copy(stripe?.webhook_url || "")} data-testid="copy-webhook-url">
                <Copy size={13} className="mr-1.5" /> Copy
              </Button>
            </div>
            <code className="block text-xs bg-white border border-border rounded p-2 break-all font-mono" data-testid="webhook-url">{stripe?.webhook_url}</code>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Listen for events: <span className="font-mono">checkout.session.completed</span>,
              <span className="font-mono">checkout.session.async_payment_succeeded</span>,
              <span className="font-mono">checkout.session.async_payment_failed</span>
            </p>
          </div>

          {/* Test button + result */}
          {isAdmin && (
            <div className="flex items-center gap-3 pt-1">
              <Button onClick={testStripe} disabled={testing || stripe?.enabled === false} size="sm" data-testid="test-stripe-btn">
                {testing ? <RefreshCw size={14} className="mr-1.5 animate-spin" /> : <ShieldCheck size={14} className="mr-1.5" />}
                Test Connection
              </Button>
              {testResult && (
                <div className={`flex items-center gap-1.5 text-xs ${testResult.ok ? "text-emerald-700" : "text-red-700"}`} data-testid="test-stripe-result">
                  {testResult.ok ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
                  {testResult.ok ? `Connected${testResult.livemode ? " (LIVE)" : " (test)"}` : (testResult.error || "Failed").slice(0, 100)}
                </div>
              )}
            </div>
          )}

          {/* Setup checklist */}
          <details className="text-xs border border-border rounded p-3" open={stripe?.mode !== "live"}>
            <summary className="font-medium cursor-pointer">Production setup checklist</summary>
            <ol className="list-decimal pl-5 mt-2 space-y-1 text-muted-foreground">
              <li>In Stripe Dashboard → Developers → API Keys, copy the <strong>Live secret key</strong> (starts with <code className="font-mono">sk_live_</code>).</li>
              <li>Set <code className="font-mono">STRIPE_API_KEY</code> in <code className="font-mono">backend/.env</code> and restart the backend.</li>
              <li>In Stripe Dashboard → Developers → Webhooks → "Add endpoint", paste the URL above and select <code className="font-mono">checkout.session.*</code> events.</li>
              <li>Reveal the <strong>Signing secret</strong> (starts with <code className="font-mono">whsec_</code>) and set <code className="font-mono">STRIPE_WEBHOOK_SECRET</code> in backend/.env, then restart.</li>
              <li>Click <strong>Test Connection</strong> above — it should report <strong>LIVE MODE</strong>.</li>
              <li>Issue a small invoice (e.g. £1) to yourself, complete checkout, and verify the invoice flips to <strong>Paid</strong> automatically.</li>
            </ol>
          </details>

          {/* Recent events audit */}
          {isAdmin && events.length > 0 && (
            <div>
              <Label className="text-xs font-medium">Recent webhook events</Label>
              <div className="mt-1 border border-border rounded overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-3 py-1.5 font-medium text-muted-foreground">Event</th>
                      <th className="text-left px-3 py-1.5 font-medium text-muted-foreground">Status</th>
                      <th className="text-left px-3 py-1.5 font-medium text-muted-foreground">Received</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((e) => (
                      <tr key={e.id || e.event_id || `${e.event_type}-${e.received_at}`} className="border-t border-border">
                        <td className="px-3 py-1.5 font-mono">{e.event_type}</td>
                        <td className="px-3 py-1.5">
                          {e.processed ? <span className="text-emerald-700">Processed</span> : <span className="text-amber-700">Pending</span>}
                          {e.error && <span className="text-red-700 ml-2">{e.error.slice(0, 50)}</span>}
                        </td>
                        <td className="px-3 py-1.5 text-muted-foreground">{e.received_at?.slice(0, 19).replace("T", " ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>
        </TabsContent>

        <TabsContent value="database" className="space-y-6 mt-0">
      {/* Database Backups - admin only */}
      {isAdmin && (
        <section className="bg-white rounded-lg border border-border" data-testid="backups-section">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Database size={18} className="text-slate-600" />
              <h2 className="font-heading font-semibold">Database Backups</h2>
              {backups?.last_success_at ? (
                <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">healthy</Badge>
              ) : (
                <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">no backups yet</Badge>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={runBackupNow} disabled={backupRunning} data-testid="run-backup-btn">
              {backupRunning ? <RefreshCw size={14} className="mr-1.5 animate-spin" /> : <PlayCircle size={14} className="mr-1.5" />}
              {backupRunning ? "Running..." : "Run backup now"}
            </Button>
          </div>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <StatusTile label="Last successful backup" value={backups?.last_success_at?.slice(0, 19).replace("T", " ") || "Never"} tone={backups?.last_success_at ? "live" : "warn"} hint={backups?.last_success_at ? "Daily scheduler runs every 24h" : "Run backup now or wait for scheduler"} />
              <StatusTile label="Off-site copy" value={backups?.remote_enabled ? (backups?.last_remote_at ? "Synced" : "Pending") : "Disabled"} tone={backups?.remote_enabled && backups?.last_remote_at ? "live" : (backups?.remote_enabled ? "warn" : "neutral")} hint={backups?.last_remote_at ? `Last upload: ${backups.last_remote_at.slice(0, 19).replace("T", " ")}` : (backups?.remote_enabled ? "Will upload on next run" : "EMERGENT_LLM_KEY not set")} />
              <StatusTile label="Retention policy" value={`${backups?.retention || 14} archives`} tone="neutral" hint={`Older archives are pruned automatically`} />
              <StatusTile label="PII encryption" value={backups?.encryption_enabled ? "ON" : "OFF"} tone={backups?.encryption_enabled ? "live" : "warn"} hint={backups?.encryption_enabled ? "Phone/address/notes encrypted at rest" : "PII_ENCRYPTION_KEY not configured"} />
            </div>
            <div className="text-xs text-muted-foreground">
              Backup folder: <code className="bg-slate-50 border border-border rounded px-1.5 py-0.5">{backups?.backup_dir || "/app/backups"}</code>
            </div>
            {backups?.recent?.length > 0 && (
              <div>
                <Label className="text-xs font-medium">Recent runs</Label>
                <div className="border border-border rounded overflow-hidden mt-1.5">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-muted-foreground">
                      <tr>
                        <th className="text-left px-3 py-1.5 font-medium">Status</th>
                        <th className="text-left px-3 py-1.5 font-medium">Started</th>
                        <th className="text-left px-3 py-1.5 font-medium">Size</th>
                        <th className="text-left px-3 py-1.5 font-medium">Off-site</th>
                        <th className="text-left px-3 py-1.5 font-medium">Triggered by</th>
                      </tr>
                    </thead>
                    <tbody>
                      {backups.recent.map((r) => (
                        <tr key={r.id} className="border-t border-border" data-testid={`backup-row-${r.id}`}>
                          <td className="px-3 py-1.5">
                            {r.status === "success" ? <span className="text-emerald-700">Success</span> : <span className="text-red-700">{r.status}{r.error ? `: ${r.error.slice(0, 60)}` : ""}</span>}
                          </td>
                          <td className="px-3 py-1.5 text-muted-foreground">{r.started_at?.slice(0, 19).replace("T", " ")}</td>
                          <td className="px-3 py-1.5">{r.size_bytes ? `${(r.size_bytes / 1024 / 1024).toFixed(2)} MiB` : "—"}</td>
                          <td className="px-3 py-1.5">
                            {r.remote?.status === "uploaded"
                              ? <span className="text-emerald-700">Uploaded</span>
                              : r.remote?.status === "failed"
                                ? <span className="text-red-700" title={r.remote.error}>Failed</span>
                                : r.remote?.status === "unavailable"
                                  ? <span className="text-slate-500">N/A</span>
                                  : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="px-3 py-1.5 text-muted-foreground">{r.triggered_by || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </section>
      )}
        </TabsContent>

        <TabsContent value="import" className="space-y-6 mt-0">
      {/* Bulk CSV Import - admin only */}
      {isAdmin && <BulkImportSection />}
      {/* Bulk PDF Certificate Import (ZIP) - admin only */}
      {isAdmin && <PdfZipImportSection />}
        </TabsContent>

        <TabsContent value="team" className="space-y-6 mt-0">
      {/* Team Members - admin/staff visible */}
      <section className="bg-white rounded-lg border border-border" data-testid="team-members-section">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <UserCog size={18} className="text-slate-600" />
            <h2 className="font-heading font-semibold">Team Members</h2>
            <Badge variant="outline" className="text-[10px]">{team.length} active</Badge>
          </div>
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={() => setInviteOpen(true)} data-testid="team-invite-btn">
              <UserPlus size={14} className="mr-1.5" /> Invite
            </Button>
          )}
        </div>
        <div className="p-5">
          {team.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No team members yet.</p>
          ) : (
            <div className="overflow-hidden border border-border rounded">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-muted-foreground border-b border-border">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Name</th>
                    <th className="text-left px-3 py-2 font-medium">Email</th>
                    <th className="text-left px-3 py-2 font-medium hidden md:table-cell">Phone</th>
                    <th className="text-left px-3 py-2 font-medium">Role</th>
                    <th className="text-left px-3 py-2 font-medium">Status</th>
                    {isSuper && <th className="text-left px-3 py-2 font-medium" title={`Access to the ${lms_name} workspace`}>{lms_short_name}</th>}
                    {isAdmin && <th className="text-right px-3 py-2 font-medium w-32">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {team.map(m => (
                    <tr key={m.id} className="border-t border-border hover:bg-slate-50/60 cursor-pointer" onClick={() => setProfileId(m.id)} data-testid={`team-row-${m.id}`}>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">{m.name?.[0]?.toUpperCase()}</div>
                          <span className="font-medium text-foreground">{m.name}</span>
                          {m.status === "archived" && <Badge variant="outline" className="text-[9px] bg-slate-100 text-slate-500">Archived</Badge>}
                          {m.mfa_enabled && <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`team-mfa-badge-${m.id}`}>MFA</Badge>}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground">{m.email}</td>
                      <td className="px-3 py-2.5 text-muted-foreground hidden md:table-cell">{m.phone || "—"}</td>
                      <td className="px-3 py-2.5"><Badge variant="outline" className={`text-[10px] capitalize ${roleBadge[m.role] || ""}`}>{m.role}</Badge></td>
                      <td className="px-3 py-2.5">
                        {m.status === "archived"
                          ? <span className="text-xs text-slate-500">Archived</span>
                          : <span className="text-xs text-emerald-700">Active</span>}
                      </td>
                      {isSuper && (
                        <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                          <Switch
                            checked={m.role === "superadmin" || m.retrofit_access === true}
                            disabled={m.role === "superadmin"}
                            onCheckedChange={(v) => toggleRetrofit(m, v)}
                            className="data-[state=checked]:bg-green-600"
                            title={m.role === "superadmin" ? "Superadmins always have access" : `${lms_name} access`}
                            data-testid={`team-retrofit-${m.id}`}
                          />
                        </td>
                      )}
                      {isAdmin && (
                        <td className="px-3 py-2.5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {canAssignRoles.length > 0 && (
                              <select
                                value={m.role}
                                disabled={locked(m)}
                                onClick={(e) => e.stopPropagation()}
                                onChange={async (e) => {
                                  e.stopPropagation();
                                  const newRole = e.target.value;
                                  if (newRole === m.role) return;
                                  if (!window.confirm(`Change ${m.name || m.email}'s role from ${m.role} to ${newRole}?`)) return;
                                  try {
                                    await api.put(`/members/${m.id}`, { role: newRole });
                                    toast.success(`Role updated to ${newRole}`);
                                    refetchTeam();
                                  } catch (err) {
                                    toast.error(formatApiError(err.response?.data?.detail) || "Role change failed");
                                  }
                                }}
                                className="h-7 text-xs border border-border rounded bg-white px-1.5"
                                data-testid={`team-role-select-${m.id}`}
                                title="Change role"
                              >
                                {canAssignRoles.filter(r => r !== "student").map(r => (
                                  <option key={r} value={r}>{r}</option>
                                ))}
                                {/* Always render the current role so admins can see (but not select) higher tiers */}
                                {!canAssignRoles.includes(m.role) && <option value={m.role}>{m.role}</option>}
                              </select>
                            )}
                            {isSuper && m.mfa_enabled && <button onClick={(e) => { e.stopPropagation(); resetMfa(m); }} className="p-1.5 rounded text-purple-700 hover:bg-purple-50" title="Reset two-factor authentication" aria-label="Reset MFA" data-testid={`team-mfa-reset-${m.id}`}><ShieldOff size={13} /></button>}
                            <button onClick={(e) => { e.stopPropagation(); setProfileId(m.id); }} className="p-1.5 rounded hover:bg-slate-100" title="Edit / view" data-testid={`team-edit-${m.id}`}><Pencil size={13} /></button>
                            {!locked(m) && <button onClick={(e) => { e.stopPropagation(); archiveMember(m); }} className={`p-1.5 rounded ${m.status === "archived" ? "hover:bg-emerald-50 text-emerald-600" : "hover:bg-amber-50 text-amber-600"}`} title={m.status === "archived" ? "Restore" : "Archive"} data-testid={`team-archive-${m.id}`}>
                              {m.status === "archived" ? <ArchiveRestore size={13} /> : <Archive size={13} />}
                            </button>}
                            {!locked(m) && <button onClick={(e) => { e.stopPropagation(); deleteMember(m); }} className="p-1.5 rounded hover:bg-red-50 text-red-500" title="Delete" data-testid={`team-delete-${m.id}`}><Trash2 size={13} /></button>}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Member Profile Modal (for team) */}
      <MemberProfile memberId={profileId} open={!!profileId} onClose={() => setProfileId(null)} onUpdate={refetchTeam} isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="invitations" className="space-y-6 mt-0">
      {/* Invitations - admin only */}
      {isAdmin && (
        <section className="bg-white rounded-lg border border-border" data-testid="invitations-section">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <UserPlus size={18} className="text-slate-600" />
              <h2 className="font-heading font-semibold">Staff Invitations</h2>
              <Badge variant="outline" className="text-[10px]">internal-only signup</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={async () => {
                if (!window.confirm("Hard-delete every used / expired / revoked invitation? Pending invitations are preserved. This cannot be undone.")) return;
                try {
                  const { data } = await api.post("/invitations/purge-completed");
                  toast.success(`Purged ${data.deleted} historical invitations`);
                  const { data: inv } = await api.get("/invitations");
                  setInvites(inv);
                } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Purge failed"); }
              }} data-testid="purge-invitations-btn">
                <Trash2 size={14} className="mr-1.5" /> Purge history
              </Button>
              <Button size="sm" onClick={() => setInviteOpen(true)} data-testid="invite-staff-btn">
                <UserPlus size={14} className="mr-1.5" /> Invite Staff
              </Button>
            </div>
          </div>
          <div className="p-5">
            {invites.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No invitations yet. Click <strong>Invite Staff</strong> to send the first one.</p>
            ) : (
              <div className="overflow-hidden border border-border rounded">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs text-muted-foreground border-b border-border">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Email</th>
                      <th className="text-left px-3 py-2 font-medium">Role</th>
                      <th className="text-left px-3 py-2 font-medium">Status</th>
                      <th className="text-left px-3 py-2 font-medium">Invited By</th>
                      <th className="text-left px-3 py-2 font-medium">Expires</th>
                      <th className="text-right px-3 py-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invites.map(inv => (
                      <tr key={inv.id} className="border-t border-border" data-testid={`invite-row-${inv.id}`}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-foreground">{inv.email}</div>
                          {inv.name && <div className="text-xs text-muted-foreground">{inv.name}</div>}
                        </td>
                        <td className="px-3 py-2"><Badge variant="outline" className="text-[10px] capitalize">{inv.role}</Badge></td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className={`text-[10px] ${
                            inv.status === "accepted" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                            inv.status === "pending" ? "bg-blue-50 text-blue-700 border-blue-200" :
                            inv.status === "expired" ? "bg-amber-50 text-amber-700 border-amber-200" :
                            "bg-slate-50 text-slate-700 border-slate-200"
                          }`}>{inv.status}</Badge>
                          {inv.email_status === "failed" && <span className="text-[10px] text-red-700 ml-1">email failed</span>}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">{inv.invited_by_name}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">{inv.expires_at?.slice(0, 10)}</td>
                        <td className="px-3 py-2 text-right">
                          {inv.status === "pending" ? (
                            <div className="flex items-center justify-end gap-1">
                              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => copyInviteLink(inv)} title="Copy invite link" data-testid={`copy-invite-${inv.id}`}>
                                <Copy size={13} />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => resendInvite(inv)} title="Resend email" data-testid={`resend-invite-${inv.id}`}>
                                <Send size={13} />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600" onClick={() => revokeInvite(inv)} title="Revoke" data-testid={`revoke-invite-${inv.id}`}>
                                <Trash2 size={13} />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-2">
                              {inv.status === "accepted" && <span className="text-[11px] text-emerald-700">Joined {inv.accepted_at?.slice(0, 10)}</span>}
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600" onClick={() => revokeInvite(inv)} title="Delete invitation" data-testid={`delete-invite-${inv.id}`}>
                                <Trash2 size={13} />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Invite a Staff Member</DialogTitle></DialogHeader>
          <form onSubmit={sendInvite} className="space-y-3">
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={inviteForm.email} onChange={(e) => setInviteForm(f => ({ ...f, email: e.target.value }))} placeholder="newperson@yourcompany.co.uk" required data-testid="invite-form-email" />
            </div>
            <div>
              <Label className="text-xs">Name (optional)</Label>
              <Input value={inviteForm.name} onChange={(e) => setInviteForm(f => ({ ...f, name: e.target.value }))} placeholder="Jane Smith" data-testid="invite-form-name" />
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={inviteForm.role} onValueChange={(v) => setInviteForm(f => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="invite-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">Admin can invite others and edit settings. Staff can manage day-to-day records.</p>
            </div>
            <div>
              <Label className="text-xs">Personal Message (optional)</Label>
              <Input value={inviteForm.message} onChange={(e) => setInviteForm(f => ({ ...f, message: e.target.value }))} placeholder="Welcome to the team!" />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded p-2.5 text-xs text-blue-900 flex items-start gap-2">
              <Mail size={13} className="mt-0.5 shrink-0" />
              <span>An email with a one-time signup link will be sent. Link expires in 7 days.</span>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setInviteOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={inviteSubmitting} data-testid="send-invite-btn">
                {inviteSubmitting ? "Sending..." : "Send Invitation"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
        </TabsContent>

        <TabsContent value="company" className="space-y-6 mt-0">
          <BrandingSettings company={company} setCompany={setCompany} disabled={!isSuper} />
      {/* Company info */}
      <section className="bg-white rounded-lg border border-border" data-testid="company-section">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <Building2 size={18} className="text-slate-600" />
          <h2 className="font-heading font-semibold">Company Information</h2>
          <Badge variant="outline" className="text-[10px]">shown on receipts, invoices & emails</Badge>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Trading Name" value={company.name} onChange={(v) => set("name", v)} disabled={!isSuper} testId="company-name" />
            <Field label="Public Email" value={company.email} onChange={(v) => set("email", v)} disabled={!isSuper} testId="company-email" />
            <Field label="Phone" value={company.phone} onChange={(v) => set("phone", v)} disabled={!isSuper} testId="company-phone" />
            <Field label="Website" value={company.website} onChange={(v) => set("website", v)} disabled={!isSuper} testId="company-website" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
            <Field label="Address Line 1" value={company.address_line1} onChange={(v) => set("address_line1", v)} disabled={!isSuper} />
            <Field label="Address Line 2" value={company.address_line2} onChange={(v) => set("address_line2", v)} disabled={!isSuper} />
            <Field label="City" value={company.city} onChange={(v) => set("city", v)} disabled={!isSuper} />
            <Field label="Postcode" value={company.postcode} onChange={(v) => set("postcode", v)} disabled={!isSuper} />
            <Field label="Country" value={company.country} onChange={(v) => set("country", v)} disabled={!isSuper} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
            <Field label="VAT Number" value={company.vat_number} onChange={(v) => set("vat_number", v)} disabled={!isSuper} testId="company-vat" placeholder="GB123456789" />
            <Field label="Company Registration No." value={company.company_registration} onChange={(v) => set("company_registration", v)} disabled={!isSuper} />
          </div>
        </div>
      </section>

      {/* Bank details */}
      <section className="bg-white rounded-lg border border-border" data-testid="bank-section">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <Banknote size={18} className="text-slate-600" />
          <h2 className="font-heading font-semibold">Bank Details for Invoices</h2>
          <Badge variant="outline" className="text-[10px]">printed on PDF invoices</Badge>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Bank Name" value={company.bank_name} onChange={(v) => set("bank_name", v)} disabled={!isSuper} testId="bank-name" />
            <Field label="Account Holder Name" value={company.bank_account_name} onChange={(v) => set("bank_account_name", v)} disabled={!isSuper} />
            <Field label="Sort Code" value={company.sort_code} onChange={(v) => set("sort_code", v)} disabled={!isSuper} placeholder="00-00-00" testId="bank-sort" />
            <Field label="Account Number" value={company.account_number} onChange={(v) => set("account_number", v)} disabled={!isSuper} testId="bank-account" />
            <Field label="IBAN (optional)" value={company.iban} onChange={(v) => set("iban", v)} disabled={!isSuper} />
            <Field label="SWIFT/BIC (optional)" value={company.swift_bic} onChange={(v) => set("swift_bic", v)} disabled={!isSuper} />
          </div>
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded p-3">
            <AlertTriangle size={14} className="text-amber-700 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-800">Bank details print on every invoice PDF. Stripe online payment is the recommended method — use bank transfer as backup only.</p>
          </div>
        </div>
      </section>

      {/* Payment terms */}
      <section className="bg-white rounded-lg border border-border">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="font-heading font-semibold">Payment Terms</h2>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Default Payment Terms (days)</Label>
              <Input type="number" min="0" value={company.payment_terms_days || 14} onChange={(e) => set("payment_terms_days", parseInt(e.target.value) || 0)} disabled={!isSuper} className="h-9" data-testid="payment-terms-days" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Notes printed on invoices</Label>
              <Input value={company.payment_terms_notes || ""} onChange={(e) => set("payment_terms_notes", e.target.value)} disabled={!isSuper} className="h-9" placeholder="e.g. Late payments charged at 8% above base rate per Late Payment of Commercial Debts Act" />
            </div>
          </div>
        </div>
      </section>

      {isSuper && (
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="save-settings-btn">
            {saving ? "Saving..." : "Save Company Info"}
          </Button>
        </div>
      )}
      {!isAdmin && (
        <p className="text-xs text-muted-foreground">View-only — only admins can edit company settings.</p>
      )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, value, onChange, disabled, placeholder, testId }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} placeholder={placeholder} className="h-9" data-testid={testId} />
    </div>
  );
}

function StatusTile({ label, value, tone, hint, testId }) {
  const toneMap = {
    live: "border-emerald-300 bg-emerald-50 text-emerald-900",
    warn: "border-amber-300 bg-amber-50 text-amber-900",
    bad: "border-red-300 bg-red-50 text-red-900",
    neutral: "border-slate-200 bg-slate-50 text-slate-700",
  };
  return (
    <div className={`rounded-lg border p-3 ${toneMap[tone]}`} data-testid={testId}>
      <p className="text-[11px] uppercase tracking-wide opacity-75">{label}</p>
      <p className="font-bold text-sm mt-1">{value}</p>
      {hint && <p className="text-[11px] mt-1 opacity-75 break-all">{hint}</p>}
    </div>
  );
}
